import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap border border-transparent rounded-[var(--btn-radius)] font-[var(--btn-font-weight)] transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--btn-focus-ring)]",
	{
		variants: {
			variant: {
				primary:
					"bg-[var(--btn-primary-bg)] text-[var(--btn-primary-fg)] border-[var(--btn-primary-border)] hover:bg-[var(--btn-primary-hover-bg)]",
				secondary:
					"bg-[var(--btn-secondary-bg)] text-[var(--btn-secondary-fg)] border-[var(--btn-secondary-border)] hover:bg-[var(--btn-secondary-hover-bg)]",
				danger:
					"bg-[var(--btn-danger-bg)] text-[var(--btn-danger-fg)] border-[var(--btn-danger-border)] hover:bg-[var(--btn-danger-hover-bg)]",
				ghost:
					"bg-[var(--btn-ghost-bg)] text-[var(--btn-ghost-fg)] hover:bg-[var(--btn-ghost-hover-bg)]",
				link:
					"bg-transparent text-[var(--btn-link-fg)] underline-offset-4 hover:underline hover:text-[var(--btn-link-hover-fg)]",
				outline:
					"bg-[var(--btn-outline-bg)] text-[var(--btn-outline-fg)] border-[var(--btn-outline-border)] hover:bg-[var(--btn-outline-hover-bg)]",
				default:
					"bg-[var(--btn-primary-bg)] text-[var(--btn-primary-fg)] border-[var(--btn-primary-border)] hover:bg-[var(--btn-primary-hover-bg)]",
			},
			size: {
				sm: "h-[var(--btn-h-sm)] px-[var(--btn-px-sm)] text-sm",
				md: "h-[var(--btn-h-md)] px-[var(--btn-px-md)] text-md",
				lg: "h-[var(--btn-h-lg)] px-[var(--btn-px-lg)] text-lg",
				icon: "size-[var(--btn-icon-size)] p-0",
			},
		},
		defaultVariants: {
			variant: "primary",
			size: "md",
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : "button";

		return (
			<Comp
				data-slot="button"
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		);
	}
);
Button.displayName = "Button";

export { Button, buttonVariants };
